/* The t() door (product_improvements_v2.md, workstream O1). Hand-rolled
 * rather than i18next/react-intl: this repo ships almost no dependencies by
 * design, bundle discipline is a tested constraint (lazyChunks.test.js), and
 * what's actually needed is lookup + interpolation + one plural rule — not a
 * library.
 *
 * Locale lives in the learn settings blob (ls.settings.locale) and rides the
 * same "towardsgm-settings-changed" event every other setting already
 * dispatches on change (App.jsx's SettingsModal, read by OpeningLedger.jsx
 * and LearnCenter.jsx) — locale doesn't get a second settings bus.
 *
 * This file is infrastructure only: locales/en.js and locales/es.js are both
 * still empty. Nothing calls t() yet. Workstreams N/J/M/K/L author their new
 * strings through it as they land; O2 extracts what already exists.
 */
import { useSyncExternalStore } from "react";
import { readAttribution } from "./attribution.js";
import { loadLearn, saveLearn } from "./learn/persist.js";
import en from "./locales/en.js";

export const SUPPORTED_LOCALES = ["en", "es"];
const FALLBACK_LOCALE = "en";
const SETTINGS_EVENT = "towardsgm-settings-changed";

const dictionaries = { en };
let locale = FALLBACK_LOCALE;
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => fn());
}

/* Pure, no module state — exercised directly with fixture dictionaries in
 * i18n.test.js rather than through the (still mostly empty) real ones. */
export function resolveKey(dict, key) {
  return key.split(".").reduce((node, part) => (node && typeof node === "object" ? node[part] : undefined), dict);
}

export function formatValue(value, params) {
  if (value && typeof value === "object") {
    const rule = params?.count === 1 ? "one" : "other";
    value = value[rule] ?? value.other;
  }
  if (value === undefined) return undefined;
  if (!params) return value;
  return Object.entries(params).reduce((s, [name, v]) => s.replaceAll(`{${name}}`, String(v)), value);
}

/* The only place this file reaches locales/es.js, written explicitly (not
 * templated from `loc`) so Vite can see the specifier and give it its own
 * chunk — lazyChunks.test.js pins that nothing else references it statically. */
async function loadEs() {
  return (await import("./locales/es.js")).default;
}

export async function setLocale(next) {
  const target = SUPPORTED_LOCALES.includes(next) ? next : FALLBACK_LOCALE;
  if (target === "es") dictionaries.es ??= await loadEs();
  locale = target;
  if (typeof document !== "undefined") document.documentElement.lang = target;
  notify();
}

export function getLocale() {
  return locale;
}

/* The locale a reader gets before choosing one: their device's, when we have
 * it. Someone who installs from the Spanish Play listing, or taps a Spanish
 * ad, would otherwise open an English app and have to find the switch in
 * Settings first. Only the primary subtag counts, so es-MX and es-419 are
 * Spanish too. Never saved: a setting is something the reader chose. */
export function deviceLocale(languages) {
  const list = languages ?? (typeof navigator === "undefined" ? [] : navigator.languages?.length ? navigator.languages : [navigator.language]);
  const primary = String(list?.[0] || "").toLowerCase().split("-")[0];
  return SUPPORTED_LOCALES.includes(primary) ? primary : FALLBACK_LOCALE;
}

/* The language of the ad that brought the reader, when its campaign names
 * one. gtm-automation ends a campaign's name with its audience, and the
 * Spanish one's is `_es` (nov26_reddit_tgm_chess_es). It beats the device:
 * on the first day 16 of the Spanish ad's 37 visitors had phones set to
 * English and got an English page under a Spanish ad. `_us` and `_intl` are
 * not languages, so they fall through to the device. */
export function campaignLocale(campaign) {
  const last = String(campaign || "").toLowerCase().split("_").at(-1);
  return SUPPORTED_LOCALES.includes(last) ? last : undefined;
}

/* The ad's language lasts as long as the attribution stash: one browser
 * session. Starting from that page is the reader taking the language they
 * were shown, so the landing calls this on a start and the next visit opens
 * in it too. A reader who already chose one, or got the device's anyway,
 * has nothing to keep. */
export function keepLocaleChoice() {
  const state = loadLearn();
  if (state.settings?.locale || locale === deviceLocale()) return;
  state.settings = { ...state.settings, locale };
  saveLearn(state);
}

/* Boots from the persisted setting, else the ad's language, else the
 * device's, and stays in sync with the setting. Call once at startup, beside
 * the existing loadLearn() boot (App.jsx). */
export function initLocale() {
  const preferred = () => loadLearn().settings?.locale || campaignLocale(readAttribution()?.campaign) || deviceLocale();
  const first = preferred();
  if (first !== locale) setLocale(first);
  if (typeof window !== "undefined") {
    window.addEventListener(SETTINGS_EVENT, () => {
      const next = preferred();
      if (next !== locale) setLocale(next);
    });
  }
}

/* The lookup every component calls. A key missing from the active locale
 * falls back to English, then to the key itself — a broken key then reads as
 * a literal "onboarding.step.welcome" on screen, which is loud on purpose
 * rather than silently blank. */
export function t(key, params) {
  const active = formatValue(resolveKey(dictionaries[locale], key), params);
  if (active !== undefined) return active;
  const fallback = formatValue(resolveKey(en, key), params);
  return fallback !== undefined ? fallback : key;
}

/* A sentence with a React node in the middle — a bolded username, a link,
 * a button. Splitting such a sentence into "before" and "after" keys is the
 * usual shortcut and it is wrong: the two halves can only ever be reassembled
 * in English word order, and the translator never sees the whole sentence to
 * know what it says. So the string stays ONE key with the hole still in it,
 * gets translated whole, and is split on the hole only at render.
 *
 *   const [before, after] = tAround("account.deleteHint", "link");
 *   <>{before}<a href="…">…</a>{after}</>
 *
 * Returns [whole, ""] when the hole is absent, so a translation that dropped
 * the placeholder degrades to plain text rather than losing the tail. */
export function tAround(key, hole, params) {
  const s = t(key, params);
  const at = s.indexOf(`{${hole}}`);
  if (at < 0) return [s, ""];
  return [s.slice(0, at), s.slice(at + hole.length + 2)];
}

/* React binding — re-renders callers once a lazy-loaded dictionary resolves
 * or the locale otherwise changes. t() itself stays a plain function so it
 * can also be called outside components (recapEmail.js, notify.js). */
export function useT() {
  useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    getLocale,
    getLocale,
  );
  return t;
}
